import { ComponentType, PropsWithChildren } from 'react';

import {
  Extension,
  attachComponentData,
  useElementFilter,
} from '@backstage/core-plugin-api';

export type EditorDataExtensionOptions = {
  id: string;
  DataProvider: ComponentType<PropsWithChildren<{}>>;
};

const EDITOR_DATA_EXTENSION_KEY = 'confidence.editor-data-extension';

/**
 * Creates an editor data extension that provides data to the Editor component.
 *
 * Each extension must provide a `DataProvider` component that wraps the editor
 * content and provides data via `EditorDataContext`.
 */
export const createEditorDataExtension = (
  options: EditorDataExtensionOptions,
): Extension<() => null> => {
  return {
    expose() {
      const EditorDataExtensionDataHolder = () => null;

      attachComponentData(
        EditorDataExtensionDataHolder,
        EDITOR_DATA_EXTENSION_KEY,
        options,
      );

      return EditorDataExtensionDataHolder;
    },
  };
};

export const EditorDataExtensions: ComponentType<PropsWithChildren<{}>> = ({
  children,
}): JSX.Element | null => {
  const extensions = useElementFilter(children, elements =>
    elements.findComponentData<EditorDataExtensionOptions>({
      key: EDITOR_DATA_EXTENSION_KEY,
    }),
  );

  (EditorDataExtensions as any).__extensions__ = extensions;

  return null;
};

/**
 * Hook to retrieve all registered editor data extensions.
 */
export const useEditorDataExtensions = (): EditorDataExtensionOptions[] => {
  return (EditorDataExtensions as any).__extensions__ || [];
};
